import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
  Divider,
  Grid,
  Alert,
  Snackbar,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  GifBox as GifIcon,
  AddPhotoAlternate as UploadIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  ColorLens as BackgroundIcon,
  Person as PersonIcon,
  Image as ImageIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatListBulleted as ListBulletedIcon,
  FormatListNumbered as ListNumberedIcon,
  FormatColorText as ColorTextIcon,
  Close as CloseIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import { toPng } from 'html-to-image';
import axios from 'axios';
import GIF from 'gif.js-upgrade';
import ReactConfetti from 'react-confetti';

// --- Add Font Whitelist --- 
const Font = Quill.import('formats/font');
const fontValues = ['arial', 'comic-sans-ms', 'courier-new', 'georgia', 'helvetica', 'lucida-sans', 'tahoma', 'times-new-roman', 'trebuchet-ms', 'verdana']; 
Font.whitelist = fontValues;
Quill.register(Font, true);

const gifSizes = [
  { value: 'square', label: 'Square', width: 1080, height: 1080, padding: 280, aspect: 1 },
  { value: 'portrait', label: 'Portrait', width: 800, height: 1000, padding: 380, aspect: 0.8 },
  { value: 'landscape', label: 'Landscape', width: 1000, height: 800, padding: 380, aspect: 1.25 }
];

const slideThemes = [
  { value: 'royal-twilight', label: 'Royal Twilight', primary: '#39286E', secondary: '#E99C2E' },
  { value: 'ocean-breeze', label: 'Ocean Breeze', primary: '#1976d2', secondary: '#ffffff' },
  { value: 'sunset-orange', label: 'Sunset Orange', primary: '#FF5722', secondary: '#FFF3E0' },
  { value: 'forest-green', label: 'Forest Green', primary: '#2e7d32', secondary: '#ffffff' },
  { value: 'rose-gold', label: 'Rose Gold', primary: '#c2185b', secondary: '#f8bbd0' },
  { value: 'dark-mode', label: 'Dark Mode', primary: '#212121', secondary: '#ffffff' },
  { value: 'deep-purple', label: 'Deep Purple', primary: '#673AB7', secondary: '#ffffff' },
  { value: 'teal-mint', label: 'Teal Mint', primary: '#009688', secondary: '#E0F2F1' },
  { value: 'crimson-gold', label: 'Crimson Gold', primary: '#B71C1C', secondary: '#FFF176' },
  { value: 'midnight-blue', label: 'Midnight Blue', primary: '#0D47A1', secondary: '#E3F2FD' },
  { value: 'amber-brown', label: 'Amber Brown', primary: '#795548', secondary: '#FFECB3' },
  { value: 'slate-gray', label: 'Slate Gray', primary: '#455A64', secondary: '#ECEFF1' },
  { value: 'white', label: 'White', primary: '#ffffff', secondary: '#4890fe' },
  { value: 'custom-color', label: 'Custom Color', primary: '#4890fe', secondary: '#ffffff', isCustom: true }
];

const backgroundStyles = [
  { 
    value: 'custom-image', 
    label: 'Custom Image',
    preview: (color, secondary, customBgImage) => ({
      backgroundColor: color,
      backgroundImage: customBgImage ? `url(${customBgImage})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    })
  },
  { 
    value: 'solid', 
    label: 'Solid Color',
    preview: (color) => ({
      backgroundColor: color
    })
  },
  { 
    value: 'gradient-linear', 
    label: 'Linear Gradient',
    preview: (color) => ({
      background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`
    })
  },
  {
    value: 'gradient-radial', 
    label: 'Radial Gradient',
    preview: (color) => ({
      background: `radial-gradient(circle, ${color} 0%, ${color}dd 100%)`
    })
  }
];

const animationStyles = [
  {
    value: 'gradient-flow',
    label: 'Gradient Flow',
    keyframes: `
      @keyframes gradient-flow {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `,
    style: {
      animation: 'gradient-flow 15s ease infinite',
      backgroundSize: '200% 200%',
      backgroundImage: 'linear-gradient(45deg, #FF6B6B, #4ECDC4, #45B7D1, #96C93D)',
    },
    preview: true
  },
  {
    value: 'circle-pulse',
    label: 'Circle Pulse',
    keyframes: `
      @keyframes circle-pulse-ring {
        0% {
          transform: scale(0.7);
          opacity: 0.3;
        }
        50% {
          opacity: 0.8;
        }
        100% {
          transform: scale(1.3);
          opacity: 0;
        }
      }
      @keyframes circle-pulse-dot {
        0% {
          transform: scale(0.9);
        }
        50% {
          transform: scale(1.1);
        }
        100% {
          transform: scale(0.9);
        }
      }
    `,
    style: {
      position: 'relative',
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        background: '#4285f4'
      },
      '&::before': {
        animation: 'circle-pulse-ring 2s ease-out infinite',
        background: 'rgba(66, 133, 244, 0.3)'
      },
      '&::after': {
        animation: 'circle-pulse-dot 2s ease-out infinite',
        background: '#4285f4'
      }
    },
    preview: true
  },
  {
    value: 'confetti',
    label: 'Confetti',
    keyframes: `
      @keyframes confetti-rain {
        0% {
          background-position: 0 0;
          opacity: 1;
        }
        100% {
          background-position: 550px 650px;
          opacity: 0.7;
        }
      }
    `,
    style: {
      background: `
        radial-gradient(circle at 50% 50%, #FFD700 2px, transparent 4px),
        radial-gradient(circle at 30% 30%, #FF69B4 2px, transparent 3px),
        radial-gradient(circle at 70% 70%, #00FF00 2px, transparent 3px),
        radial-gradient(circle at 20% 80%, #FF4500 2px, transparent 4px),
        radial-gradient(circle at 80% 20%, #00BFFF 2px, transparent 3px)
      `,
      backgroundSize: '50px 50px',
      animation: 'confetti-rain 3s linear infinite',
      backgroundColor: '#f8f9fa'
    },
    preview: true
  },
  {
    value: 'rainbow-wave',
    label: 'Rainbow Wave',
    keyframes: `
      @keyframes rainbow-wave {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `,
    style: {
      animation: 'rainbow-wave 8s ease infinite',
      backgroundSize: '300% 300%',
      backgroundImage: 'linear-gradient(45deg, #ff9a9e, #fad0c4, #fad0c4, #a18cd1, #fbc2eb)',
    },
    preview: true
  },
  {
    value: 'neon-pulse',
    label: 'Neon Pulse',
    keyframes: `
      @keyframes neon-pulse {
        0%, 100% {
          filter: drop-shadow(0 0 5px #fff)
                 drop-shadow(0 0 10px #fff)
                 drop-shadow(0 0 15px #ff1177)
                 drop-shadow(0 0 20px #ff1177);
          opacity: 1;
        }
        50% {
          filter: drop-shadow(0 0 2px #fff)
                 drop-shadow(0 0 4px #fff)
                 drop-shadow(0 0 7px #ff1177)
                 drop-shadow(0 0 10px #ff1177);
          opacity: 0.8;
        }
      }
    `,
    style: {
      animation: 'neon-pulse 1.5s ease-in-out infinite',
      background: '#1a1a1a',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '4px',
        background: '#ff1177',
        borderRadius: '4px'
      }
    },
    preview: true
  },
  {
    value: 'geometric-morph',
    label: 'Geometric Morph',
    keyframes: `
      @keyframes geometric-morph {
        0%, 100% { 
          clipPath: 'circle(50% at 50% 50%)';
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        33% { 
          clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)';
          background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
        }
        66% { 
          clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%)';
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
      }
    `,
    style: {
      animation: 'geometric-morph 8s ease infinite',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    preview: true
  },
  {
    value: 'particle-field',
    label: 'Particle Field',
    keyframes: `
      @keyframes particle-move {
        0% {
          background-position: 0% 0%;
          opacity: 0.5;
        }
        50% {
          opacity: 1;
        }
        100% {
          background-position: 100% 100%;
          opacity: 0.5;
        }
      }
    `,
    style: {
      background: `
        radial-gradient(circle at center, white 1px, transparent 1px),
        radial-gradient(circle at center, white 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px, 30px 30px',
      backgroundColor: '#000',
      animation: 'particle-move 4s ease-in-out infinite',
    },
    preview: true
  },
  {
    value: 'ripple',
    label: 'Ripple',
    keyframes: `
      @keyframes ripple {
        0% {
          transform: scale(0.3);
          opacity: 0.8;
        }
        100% {
          transform: scale(3);
          opacity: 0;
        }
      }
    `,
    style: {
      position: 'relative',
      background: '#4285f4',
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        background: 'inherit',
        animation: 'ripple 3s infinite cubic-bezier(0.3, 0, 0.2, 1)',
        zIndex: 0
      },
      '&::after': {
        animationDelay: '1s'
      }
    },
    preview: true
  }
];

// Move GifPreview component outside
const GifPreview = ({ 
  gifText, 
  gifName, 
  gifSize,
  selectedTheme,
  selectedStyle,
  customBgImage,
  selectedAnimation,
  contentType,
  gifImage,
  previewRef,
  forceRender,
  imageContentScale
}) => {
  const theme = slideThemes.find(t => t.value === selectedTheme);
  const style = backgroundStyles.find(s => s.value === selectedStyle);
  const currentTheme = theme || slideThemes[0];
  const currentStyle = style || backgroundStyles[0];
  const currentSize = gifSizes.find(s => s.value === gifSize) || gifSizes[0];
  const currentAnimation = animationStyles.find(a => a.value === selectedAnimation) || animationStyles[0];
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Track container dimensions for confetti
  useEffect(() => {
    if (containerRef.current && selectedAnimation === 'confetti') {
      const updateDimensions = () => {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      };
      
      updateDimensions();
      window.addEventListener('resize', updateDimensions);
      
      return () => window.removeEventListener('resize', updateDimensions);
    }
  }, [selectedAnimation, forceRender]);

  // Add animation styles to document
  useEffect(() => {
    const styleId = 'gif-animations';
    let styleSheet = document.getElementById(styleId);
    
    if (!styleSheet) {
      styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      document.head.appendChild(styleSheet);
    }

    const keyframes = animationStyles.map(style => style.keyframes).join('\n');
    styleSheet.textContent = keyframes;

    return () => {
      if (styleSheet && styleSheet.parentNode) {
        styleSheet.parentNode.removeChild(styleSheet);
      }
    };
  }, []);

  const formatGifText = (text) => {
    if (!text) return 'Your GIF text will appear here...';
    return text;
  };

  const getBackgroundStyles = () => {
    const bgStyles = {};
    
    // Set base background styles
    if (selectedStyle === 'custom-image' && customBgImage) {
      bgStyles.backgroundImage = `url("${customBgImage}")`;
      bgStyles.backgroundSize = 'cover';
      bgStyles.backgroundPosition = 'center';
      bgStyles.backgroundRepeat = 'no-repeat';
      bgStyles.backgroundColor = currentTheme.primary;
    } else {
      Object.assign(bgStyles, currentStyle.preview(currentTheme.primary, currentTheme.secondary));
    }

    // If using real confetti, we don't need the default confetti animation background
    if (selectedAnimation !== 'confetti') {
    // Apply animation styles
    if (currentAnimation.style.backgroundImage) {
      bgStyles.backgroundImage = typeof currentAnimation.style.backgroundImage === 'function'
        ? currentAnimation.style.backgroundImage(currentTheme.primary)
        : currentAnimation.style.backgroundImage;
    }
    
    if (currentAnimation.style.backgroundSize) {
      bgStyles.backgroundSize = currentAnimation.style.backgroundSize;
    }

    // Add animation and performance properties
    bgStyles.animation = currentAnimation.style.animation;
    }
    
    // Common styles
    bgStyles.transform = 'translate3d(0,0,0)';
    bgStyles.backfaceVisibility = 'hidden';
    bgStyles.willChange = 'transform, opacity, background-position';

    return bgStyles;
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
      <Box
        ref={(el) => {
          // Assign to both refs
          previewRef && (previewRef.current = el);
          containerRef.current = el;
        }}
        className="gif-preview-container"
        sx={{
          position: 'relative',
          width: '90%',
          maxWidth: '550px',
          aspectRatio: `${currentSize.aspect}`,
          borderRadius: '0px',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: 'translate3d(0,0,0)',
          backfaceVisibility: 'hidden'
        }}
      >
        {/* Animated Background Layer */}
        <Box
          key={`${selectedAnimation}-${selectedStyle}-${selectedTheme}-${forceRender}`}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '0px',
            ...getBackgroundStyles()
          }}
        />

        {/* Real Confetti Layer */}
        {selectedAnimation === 'confetti' && dimensions.width > 0 && (
          <ReactConfetti
            width={dimensions.width}
            height={dimensions.height}
            recycle={true}
            numberOfPieces={75}
            tweenDuration={5000}
            colors={['#4890fe', '#3a7ad9', '#a864fd', '#ff718d', '#fdff6a', '#C0C0C0', '#E63946']}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 2
            }}
          />
        )}

        {/* Content Layer */}
        <Box
          className="gif-content"
          sx={{ 
            width: contentType === 'image' ? '90%' : '80%',
            maxHeight: contentType === 'image' ? '90%' : '80%',
            backgroundColor: contentType === 'image' ? 'rgba(255, 255, 255, 0.95)' : '#ffffff',
            borderRadius: '5px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            p: contentType === 'image' ? 1 : 3,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: contentType === 'image' ? `scale(${imageContentScale})` : 'none',
            transformOrigin: 'center center',
            transition: 'transform 0.2s ease-out',
          }}
        >
          {contentType === 'text' ? (
            <Box 
              sx={{ 
                fontSize: '15px',
                color: '#0f1419',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.4,
                textAlign: 'center',
                width: '100%',
                '& p': { margin: 0, padding: 0 },
                '& ol, & ul': { paddingLeft: '1.5em' },
                '& a': { color: '#1d9bf0', textDecoration: 'none' },
                '& strong, & b': { fontWeight: 'bold' },
                '& em, & i': { fontStyle: 'italic' }
              }}
              dangerouslySetInnerHTML={{ __html: formatGifText(gifText) }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
                borderRadius: '6px',
                overflow: 'hidden'
              }}
            >
              <Box
                component="img"
                src={gifImage}
                alt="GIF content"
                sx={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: '4px',
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Add this new component for animation previews
const AnimationPreview = ({ animation, size = 100 }) => {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
        ...animation.style,
      }}
    />
  );
};

const GifGenerator = forwardRef(({
  onSaveToPost, 
  onStateChange, 
  showPreview = false, 
  previewOnly = false,
  gifText: propGifText = '<p>Create amazing animated GIFs with Instation</p>',
  gifName: propGifName = 'Instation',
  gifSize: propGifSize = 'square',
  selectedTheme: propSelectedTheme = 'deep-purple',
  selectedStyle: propSelectedStyle = 'gradient-linear',
  customBgImage: propCustomBgImage = null,
  selectedAnimation: propSelectedAnimation = 'gradient',
  contentType: propContentType = 'text',
  gifImage: propGifImage = null,
  imageContentScale: propImageContentScale = 1,
}, ref) => {
  const [gifText, setGifText] = useState(propGifText);
  const [gifName, setGifName] = useState(propGifName);
  const [gifSize, setGifSize] = useState(propGifSize);
  const [selectedTheme, setSelectedTheme] = useState(propSelectedTheme);
  const [selectedStyle, setSelectedStyle] = useState(propSelectedStyle);
  const [customBgImage, setCustomBgImage] = useState(propCustomBgImage);
  const [selectedAnimation, setSelectedAnimation] = useState(propSelectedAnimation);
  const [contentType, setContentType] = useState(propContentType);
  const [gifImage, setGifImage] = useState(propGifImage);
  const [imageContentScale, setImageContentScale] = useState(propImageContentScale);
  const [sliderValue, setSliderValue] = useState(propImageContentScale);
  const [forceRender, setForceRender] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customPrimaryColor, setCustomPrimaryColor] = useState('#4890fe');
  const [customSecondaryColor, setCustomSecondaryColor] = useState('#ffffff');
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const containerRef = useRef(null);
  const internalPreviewRef = useRef(null);
  const activePreviewRef = internalPreviewRef;
  const isUpdatingRef = useRef(false);

  // Callback for progress updates
  const [progressCallback, setProgressCallback] = useState(null);

  // Update state when props change
  useEffect(() => {
    if (isUpdatingRef.current) return;

    setGifText(propGifText);
    setGifName(propGifName);
    setGifSize(propGifSize);
    setSelectedTheme(propSelectedTheme);
    setSelectedStyle(propSelectedStyle);
    setCustomBgImage(propCustomBgImage);
    setSelectedAnimation(propSelectedAnimation);
    setContentType(propContentType);
    setGifImage(propGifImage);
    setImageContentScale(propImageContentScale);
    setSliderValue(propImageContentScale);
  }, [propGifText, propGifName, propGifSize, propSelectedTheme, propSelectedStyle, propCustomBgImage, propSelectedAnimation, propContentType, propGifImage, propImageContentScale]);

  // Debounce effect for imageContentScale
  useEffect(() => {
    const handler = setTimeout(() => {
      setImageContentScale(sliderValue);
    }, 100); // Adjust delay as needed (e.g., 50-150ms)

    return () => {
      clearTimeout(handler);
    };
  }, [sliderValue]);

  // Notify parent of state changes
  const notifyStateChange = useCallback(() => {
    if (!onStateChange || isUpdatingRef.current) return;

    isUpdatingRef.current = true;
    const currentState = {
      gifText,
      gifName,
      gifSize,
      selectedTheme,
      selectedStyle,
      customBgImage,
      selectedAnimation,
      contentType,
      gifImage,
      imageContentScale,
    };
    
    onStateChange(currentState);
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, [gifText, gifName, gifSize, selectedTheme, selectedStyle, customBgImage, selectedAnimation, contentType, gifImage, imageContentScale, onStateChange]);

  useEffect(() => {
    notifyStateChange();
  }, [notifyStateChange]);

  const handleSizeChange = (e, newSize) => {
    if (newSize) setGifSize(newSize);
  };

  const handleThemeChange = (e) => {
    const newTheme = e.target.value;
    console.log('Changing theme to:', newTheme);
    setSelectedTheme(newTheme);
    
    // If custom color is selected, open the color picker
    if (newTheme === 'custom-color') {
      setColorPickerOpen(true);
    }

    if (onStateChange) {
      onStateChange({
        gifText,
        gifName,
        gifSize,
        selectedTheme: newTheme,
        selectedStyle,
        customBgImage,
        selectedAnimation,
        contentType,
        gifImage,
        imageContentScale,
      });
    }
  };

  const handleStyleChange = (style) => {
    console.log('Changing style to:', style);
    setSelectedStyle(style);

    if (onStateChange) {
      onStateChange({
        gifText,
        gifName,
        gifSize,
        selectedTheme,
        selectedStyle: style,
        customBgImage,
        selectedAnimation,
        contentType,
        gifImage,
        imageContentScale,
      });
    }
  };

  const handleBackgroundImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      console.log('Background image loaded:', imageUrl);
      setCustomBgImage(imageUrl);
      setSelectedStyle('custom-image');
      setForceRender(prev => prev + 1);

      if (onStateChange) {
        onStateChange({
          gifText,
          gifName,
          gifSize,
          selectedTheme,
          selectedStyle: 'custom-image',
          customBgImage: imageUrl,
          selectedAnimation,
          contentType,
          gifImage,
          imageContentScale,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleContentTypeChange = (event) => {
    const newType = event.target.value;
    setContentType(newType);
    setForceRender(prev => prev + 1);
    
    if (onStateChange) {
      onStateChange({
        gifText,
        gifName,
        gifSize,
        selectedTheme,
        selectedStyle,
        customBgImage,
        selectedAnimation,
        contentType: newType,
        gifImage,
        imageContentScale,
      });
    }
  };

  const handleGifImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setGifImage(imageUrl);
      setContentType('image');
      setForceRender(prev => prev + 1);
      
      if (onStateChange) {
        onStateChange({
          gifText,
          gifName,
          gifSize,
          selectedTheme,
          selectedStyle,
          customBgImage,
          selectedAnimation,
          contentType: 'image',
          gifImage: imageUrl,
          imageContentScale,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // Handle custom color change
  const handleCustomColorChange = (isPrimary, color) => {
    if (isPrimary) {
      setCustomPrimaryColor(color);
    } else {
      setCustomSecondaryColor(color);
    }
    
    // Update the custom color theme
    const updatedThemes = slideThemes.map(theme => {
      if (theme.value === 'custom-color') {
        return {
          ...theme,
          primary: isPrimary ? color : customPrimaryColor,
          secondary: isPrimary ? customSecondaryColor : color
        };
      }
      return theme;
    });
    
    // Force update by triggering rerender
    setForceRender(prev => prev + 1);
    
    if (onStateChange && selectedTheme === 'custom-color') {
      onStateChange({
        gifText,
        gifName,
        gifSize,
        selectedTheme,
        selectedStyle,
        customBgImage,
        selectedAnimation,
        contentType,
        gifImage,
        imageContentScale,
      });
    }
  };

  useImperativeHandle(ref, () => ({
    handleDownload: (onProgress) => {
      setProgressCallback(() => onProgress); // Store the callback
      return handleDownloadInternal();
    },
    handleSaveToPost: (onProgress) => {
      setProgressCallback(() => onProgress); // Store the callback
      return handleSaveToPostInternal();
    }
  }));

  const captureFrames = async (container, duration = 2000, fps = 10) => {
    const frames = [];
    const frameCount = Math.floor((duration / 1000) * fps);
    const frameDelay = Math.floor(1000 / fps);
    const currentSizeDetails = gifSizes.find(s => s.value === gifSize) || gifSizes[0];
    
    try {
      // Create a canvas for frame capture
      const canvas = document.createElement('canvas');
      canvas.width = Math.min(container.offsetWidth, currentSizeDetails.width);
      canvas.height = Math.min(container.offsetHeight, currentSizeDetails.height);
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // First frame
      const initialFrame = await toPng(container, {
        quality: 1.0,
        pixelRatio: 2,
        skipFonts: true,
        cacheBust: true,
        width: canvas.width,
        height: canvas.height,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'center'
        }
      });

      // Load first frame
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          frames.push(canvas.toDataURL('image/png'));
          resolve();
        };
        img.onerror = reject;
        img.src = initialFrame;
      });
      
      // Capture remaining frames
      for (let i = 1; i < frameCount; i++) {
        await new Promise(resolve => setTimeout(resolve, frameDelay));
        
        const dataUrl = await toPng(container, {
          quality: 1.0,
          pixelRatio: 2,
          skipFonts: true,
          cacheBust: true,
          width: canvas.width,
          height: canvas.height,
          style: {
            transform: 'scale(1)',
            transformOrigin: 'center'
          }
        });

        // Load and draw frame
        await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            frames.push(canvas.toDataURL('image/png'));
            resolve();
          };
          img.onerror = reject;
          img.src = dataUrl;
        });
        
        console.log(`Captured frame ${i + 1} of ${frameCount}`);
      }
      
      return frames;
    } catch (error) {
      console.error('Error capturing frames:', error);
      throw new Error('Failed to capture animation frames');
    }
  };

  const createGif = (frames, width, height, onProgress) => {
    return new Promise((resolve, reject) => {
      try {
        console.log('Creating GIF with dimensions:', { width, height });
        const currentSizeDetails = gifSizes.find(s => s.value === gifSize) || gifSizes[0];
        const gif = new GIF({
          workers: 2,
          quality: 5,
          width: Math.min(width, currentSizeDetails.width),
          height: Math.min(height, currentSizeDetails.height),
          workerScript: process.env.PUBLIC_URL + '/gif.worker.js',
          background: '#ffffff',
          transparent: null,
          debug: true
        });

        let framesAdded = 0;
        const totalFrames = frames.length;

        gif.on('progress', progress => {
          console.log(`GIF encoding progress: ${Math.round(progress * 100)}%`);
          if (onProgress) {
            onProgress(Math.round(progress * 100)); // Call the progress callback
          }
        });

        gif.on('finished', blob => {
          console.log('GIF created successfully:', {
            size: blob.size,
            frames: framesAdded,
            width,
            height
          });
          resolve(blob);
        });

        gif.on('error', error => {
          console.error('GIF creation error:', error);
          reject(new Error('Failed to create GIF: ' + error.message));
        });

        frames.forEach((frameDataUrl, index) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = Math.min(width, currentSizeDetails.width);
            canvas.height = Math.min(height, currentSizeDetails.height);
            const ctx = canvas.getContext('2d');
            
            if (ctx) {
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              gif.addFrame(canvas, {
                delay: 100,
                copy: true,
                dispose: 2
              });
              framesAdded++;
              console.log(`Added frame ${framesAdded} of ${totalFrames}`);

              if (framesAdded === totalFrames) {
                console.log(`Starting GIF render with ${framesAdded} frames...`);
                gif.render();
              }
            }
          };
          img.src = frameDataUrl;
        });
      } catch (error) {
        console.error('Error in createGif:', error);
        reject(error);
      }
    });
  };

  const handleDownloadInternal = async () => {
    if (downloading) return;
    
    try {
      setDownloading(true);
      
      let previewContainer = document.querySelector('.gif-preview-container');
      
      if (!previewContainer && activePreviewRef && activePreviewRef.current) {
        previewContainer = activePreviewRef.current;
      }
      
      if (!previewContainer) {
        throw new Error('GIF preview container not found');
      }

      const currentSizeDetails = gifSizes.find(s => s.value === gifSize) || gifSizes[0];
      const containerWidth = currentSizeDetails.width;
      const containerHeight = currentSizeDetails.height;

      console.log('Starting frame capture...', {
        width: containerWidth,
        height: containerHeight
      });

      const frames = await captureFrames(previewContainer);
      console.log('Frames captured:', frames.length);
      
      if (frames.length === 0) {
        throw new Error('No frames were captured');
      }

      console.log('Creating GIF...');
      const gifBlob = await createGif(frames, containerWidth, containerHeight, progressCallback);
      
      if (!gifBlob || gifBlob.size === 0) {
        throw new Error('Generated GIF is empty');
      }

      console.log('GIF created, size:', gifBlob.size);
      
      const link = document.createElement('a');
      link.download = `animated-gif-${Date.now()}.gif`;
      link.href = URL.createObjectURL(gifBlob);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
    } catch (error) {
      console.error('Error generating GIF:', error);
      alert('There was an error generating the GIF. Please try again. Error: ' + error.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveToPostInternal = async () => {
    try {
      setSaving(true);
      
      let previewContainer = document.querySelector('.gif-preview-container');
      
      if (!previewContainer && activePreviewRef && activePreviewRef.current) {
        previewContainer = activePreviewRef.current;
      }
      
      if (!previewContainer) {
        throw new Error('GIF preview container not found');
      }

      const frames = await captureFrames(previewContainer);
      const gifBlob = await createGif(
        frames,
        previewContainer.offsetWidth,
        previewContainer.offsetHeight,
        progressCallback
      );
      
      if (onSaveToPost) {
        onSaveToPost(gifBlob);
      }
      
    } catch (error) {
      console.error('Error preparing GIF:', error);
      alert('There was an error preparing the GIF. Please try again. Error: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const currentSize = gifSizes.find(s => s.value === gifSize) || gifSizes[0];

  // Update renderPreview to use the GifPreview component directly
  const renderPreview = () => (
    <GifPreview
      key={`preview-${contentType}-${gifImage}-${forceRender}`}
      gifText={previewOnly ? propGifText : gifText}
      gifName={previewOnly ? propGifName : gifName}
      gifSize={previewOnly ? propGifSize : gifSize}
      selectedTheme={selectedTheme}
      selectedStyle={selectedStyle}
      customBgImage={customBgImage}
      selectedAnimation={selectedAnimation}
      contentType={contentType}
      gifImage={gifImage}
      previewRef={activePreviewRef}
      forceRender={forceRender}
      imageContentScale={previewOnly ? propImageContentScale : imageContentScale}
    />
  );

  // Add animation preview grid
  const renderAnimationPreviews = () => (
    <Grid container spacing={1} sx={{ mb: 2.5 }}>
      {animationStyles.map(animation => (
        <Grid item xs={6} sm={3} key={animation.value}>
          <Box
            onClick={() => setSelectedAnimation(animation.value)}
            sx={{ 
              cursor: 'pointer',
              border: `2px solid ${selectedAnimation === animation.value ? 'var(--primary-color)' : 'transparent'}`,
              borderRadius: '8px',
              overflow: 'hidden',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }
            }}
          >
            <Box sx={{ padding: '75%', position: 'relative' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                <AnimationPreview animation={animation} />
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                py: 0.5,
                px: 1,
                bgcolor: 'background.paper',
                borderTop: '1px solid rgba(0,0,0,0.05)',
                fontWeight: selectedAnimation === animation.value ? 600 : 400
              }}
            >
              {animation.label}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  if (previewOnly) {
    return (
      <Box ref={containerRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}>
        <Box sx={{
          width: '100%',
          maxWidth: '550px',
          height: 'auto',
          position: 'relative',
          '&::before': {
            content: '""',
            display: 'block',
            paddingTop: `${(currentSize.height / currentSize.width) * 100}%`,
          }
        }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {renderPreview()}
          </Box>
        </Box>
      </Box>
    );
  }

  const quillModules = {
    toolbar: [
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'font': fontValues }],
      ['bold', 'italic', 'underline', 'strike'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'size', 'font',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link'
  ];

  return (
    <Box ref={containerRef} sx={{ display: 'flex', flexDirection: { xs: 'column', md: showPreview ? 'column' : 'row' }, gap: 2, width: '100%', height: '100%' }}>
      {/* Color Picker Dialog */}
      <Dialog open={colorPickerOpen} onClose={() => setColorPickerOpen(false)}>
        <DialogTitle>Choose Custom Colors</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Primary Color</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: '4px', 
                  backgroundColor: customPrimaryColor,
                  border: '1px solid rgba(0,0,0,0.1)',
                  mr: 2
                }}
              />
              <input
                type="color"
                value={customPrimaryColor}
                onChange={(e) => handleCustomColorChange(true, e.target.value)}
                style={{ width: '100%', height: 40 }}
              />
            </Box>
            
            <Typography variant="subtitle2" gutterBottom>Secondary Color</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: '4px', 
                  backgroundColor: customSecondaryColor,
                  border: '1px solid rgba(0,0,0,0.1)',
                  mr: 2
                }}
              />
              <input
                type="color"
                value={customSecondaryColor}
                onChange={(e) => handleCustomColorChange(false, e.target.value)}
                style={{ width: '100%', height: 40 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColorPickerOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
      
      {/* Control Panel */}
      <Box sx={{
        flex: showPreview ? 1 : '1',
        display: 'flex',
        flexDirection: 'column',
        px: 1.5, pb: 1.5,
        borderRight: showPreview ? 'none' : '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <Box sx={{ flex: 1, pr: 0.5 }}>
          {/* Content Type Selection */}
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: '#4A5568', fontSize: '0.95rem' }}>
            Content Type
          </Typography>
          <ToggleButtonGroup
            value={contentType}
            exclusive
            onChange={(e, newValue) => newValue && handleContentTypeChange({ target: { value: newValue } })}
            aria-label="content type"
            fullWidth
            size="small"
            sx={{ mb: 2.5 }}
          >
            <ToggleButton value="text">
              Text
            </ToggleButton>
            <ToggleButton value="image">
              Image
            </ToggleButton>
          </ToggleButtonGroup>

          {/* GIF Content */}
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: '#4A5568', fontSize: '0.95rem' }}>
            GIF Content
          </Typography>
          
          {contentType === 'text' ? (
            <Box sx={{ mb: 3 }}>
              <ReactQuill 
                theme="snow" 
                value={gifText}
                onChange={setGifText} 
                modules={quillModules}
                formats={quillFormats}
                placeholder="Enter your GIF text here..."
                className="gif-editor" 
              />
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <input
                accept="image/*"
                id="gif-image-upload"
                type="file"
                style={{ display: 'none' }}
                onChange={handleGifImageUpload}
              />
              <label htmlFor="gif-image-upload">
                <Button
                  component="span"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ 
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                    color: '#4A5568',
                    '&:hover': {
                      borderColor: 'var(--primary-color)',
                      backgroundColor: 'rgba(72, 144, 254, 0.04)'
                    }
                  }}
                >
                  Upload Image
                </Button>
              </label>

              {gifImage && (
                <Box sx={{ mt: 2, width: '100%', position: 'relative' }}>
                  <Box
                    component="img"
                    src={gifImage}
                    alt="GIF content preview"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      display: 'block'
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      setGifImage(null);
                      setContentType('text');
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 1)'
                      }
                    }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* Image Scale Slider - New UI */}
              {contentType === 'image' && gifImage && (
                <Box sx={{ mt: 2, mb: 2.5, px: 1 }}>
                  <Typography gutterBottom sx={{ fontWeight: 500, color: '#4A5568', fontSize: '0.85rem' }}>
                    Image Scale: {Math.round(imageContentScale * 100)}%
                  </Typography>
                  <Slider
                    value={sliderValue}
                    min={0.5}
                    max={2}
                    step={0.05}
                    onChange={(e, newValue) => setSliderValue(newValue)}
                    aria-labelledby="image-scale-slider"
                    valueLabelDisplay="auto"
                    valueLabelFormat={value => `${Math.round(value * 100)}%`}
                    sx={{ 
                      color: 'var(--primary-color)',
                      '& .MuiSlider-thumb': {
                        backgroundColor: 'var(--primary-color)',
                      },
                      '& .MuiSlider-rail': {
                        color: 'rgba(0, 0, 0, 0.2)'
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Image Settings */}
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#4A5568', fontSize: '0.95rem' }}>
            GIF Settings
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: '#4A5568', fontSize: '0.85rem' }}>
            Size
          </Typography>
          <ToggleButtonGroup
            value={gifSize}
            exclusive
            onChange={handleSizeChange}
            aria-label="gif size"
            fullWidth
            size="small" 
            sx={{ mb: 2.5 }}
          >
            {gifSizes.map(size => (
              <ToggleButton key={size.value} value={size.value}>
                {size.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#4A5568', fontSize: '0.95rem' }}>
            Background Theme
          </Typography>
          <Box sx={{ display: 'flex', mb: 1 }}>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
            <Select
              value={selectedTheme}
              onChange={handleThemeChange}
              sx={{ 
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--primary-color)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--primary-color)',
                }
              }}
            >
              {slideThemes.map(theme => (
                <MenuItem key={theme.value} value={theme.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', mr: 1.5, alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: theme.primary, borderRadius: '50%' }} />
                      <Box sx={{ width: 16, height: 16, bgcolor: theme.secondary, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.1)' }} />
                    </Box>
                    {theme.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
            
            {selectedTheme === 'custom-color' && (
              <IconButton 
                onClick={() => setColorPickerOpen(true)}
                sx={{ ml: 1, bgcolor: 'rgba(0,0,0,0.04)' }}
              >
                <PaletteIcon />
              </IconButton>
            )}
          </Box>

          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#4A5568', fontSize: '0.95rem' }}>
            Background Style
          </Typography>

          {/* Custom Image Upload Section */}
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <input
              accept="image/*"
              id="background-image-upload"
              type="file"
              style={{ display: 'none' }}
              onChange={handleBackgroundImageChange}
            />
            <label htmlFor="background-image-upload">
              <Button
                component="span"
                variant="outlined"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ 
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                  color: '#4A5568',
                  '&:hover': {
                    borderColor: 'var(--primary-color)',
                    backgroundColor: 'rgba(72, 144, 254, 0.04)'
                  }
                }}
              >
                Upload Background Image
              </Button>
            </label>

            {customBgImage && (
              <Box sx={{ width: '100%', height: '100px', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0, 0, 0, 0.1)', position: 'relative' }}>
                <Box
                  component="img"
                  src={customBgImage}
                  alt="Background preview"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  onClick={() => {
                    setCustomBgImage(null);
                    if (selectedStyle === 'custom-image') {
                      setSelectedStyle('solid');
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)'
                    }
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Background Styles Grid */}
          <Grid container spacing={1} sx={{ mb: 2.5 }}>
            {backgroundStyles.filter(style => style.value !== 'custom-image').map(style => {
              const currentTheme = slideThemes.find(t => t.value === selectedTheme) || slideThemes[0];
              return (
                <Grid item xs={6} sm={3} key={style.value}>
                  <Box
                    onClick={() => handleStyleChange(style.value)}
                    sx={{ 
                      cursor: 'pointer',
                      border: `2px solid ${selectedStyle === style.value ? 'var(--primary-color)' : 'transparent'}`,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        paddingTop: '75%',
                        position: 'relative',
                        ...style.preview(currentTheme.primary, currentTheme.secondary)
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        textAlign: 'center',
                        py: 0.5,
                        px: 1,
                        bgcolor: 'background.paper',
                        borderTop: '1px solid rgba(0,0,0,0.05)',
                        fontWeight: selectedStyle === style.value ? 600 : 400
                      }}
                    >
                      {style.label}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ height: '8px' }} />

          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#4A5568', fontSize: '0.95rem' }}>
            Animation Style
          </Typography>
          {renderAnimationPreviews()}
        </Box>
      </Box>

      {/* Preview Panel */}
      {showPreview && (
        <Box sx={{ flex: '1', display: 'flex', flexDirection: 'column', mt: 2, position: 'relative', width: showPreview ? '100%' : '50%' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#4A5568', display: 'flex', alignItems: 'center', gap: 1 }}>
            <GifIcon sx={{ fontSize: 20, color: '#1DA1F2' }} />
            GIF Preview
          </Typography>
          <Box sx={{ flex: 1, border: '1px solid rgba(0, 0, 0, 0.05)', borderRadius: 2, overflow: 'hidden', position: 'relative', height: '100%', minHeight: 300 }}>
            {renderPreview()}
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default GifGenerator; 